import { motion } from "framer-motion";
import { ArrowRight, Globe, Banknote, Gavel, Stethoscope, Route } from "lucide-react";
import { Section, SectionHeader } from "./shared";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/hooks/useLanguage";
import { uiT } from "@/i18n/ui";
import type { SolutionData } from "@/data/solutionsData";

const pillarIconMap: Record<string, typeof Globe> = {
  "Funding & Investment": Banknote,
  "Regulatory & Compliance": Gavel,
  "Product Development": Stethoscope,
  "Go-to-Market Strategy": Route,
};

interface Props {
  solution: SolutionData;
  onCtaClick: () => void;
}

const SolutionSteps = ({ solution, onCtaClick }: Props) => {
  const lang = useLanguage();
  const t = uiT(lang);

  if (solution.steps.length === 0) return null;

  return (
    <Section bg>
      <div className="relative mb-2">
        <SectionHeader label={({ en: "Coordinated workstreams", es: "Áreas de trabajo coordinadas", "zh-cn": "协同工作领域" }[lang])} title={solution.stepsTitle || t("ourStructuredApproach")} icon={Route} />
        {solution.stepsSubtitle && (
          <p className="text-muted-foreground max-w-3xl mt-3 mb-6 leading-relaxed text-sm">
            {solution.stepsSubtitle}
          </p>
        )}
      </div>

      <div>
        <div className={`grid grid-cols-1 sm:grid-cols-2 ${solution.steps.length <= 2 ? 'lg:grid-cols-2 max-w-2xl mx-auto' : solution.steps.length === 3 ? 'lg:grid-cols-3 max-w-4xl mx-auto' : solution.steps.length <= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-4 justify-items-center`}>
          {solution.steps.map((step, i) => {
            const StepIcon = step.icon || pillarIconMap[step.title];
            return (
              <motion.div
                key={i}
                className="relative bg-primary/[0.04] border border-primary/10 rounded-2xl p-6 flex flex-col h-full transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
              >
                <div className="flex items-start justify-between mb-5">
                  {StepIcon && <StepIcon size={36} className="text-primary" strokeWidth={1.4} />}
                </div>
                <h3 className="text-base font-bold text-foreground mb-1">{step.title}</h3>
                {step.subtitle && (
                  <p className="text-sm font-semibold text-primary mb-3">{step.subtitle}</p>
                )}
                <p className="text-sm text-muted-foreground leading-relaxed flex-1">
                  {step.desc.split("\n\n")[0].replace(/\*\*(.*?)\*\*/g, "$1")}
                </p>
                {step.tagline && (
                  <div className="border-t border-border mt-4 pt-3">
                    <p className="text-sm italic text-muted-foreground leading-relaxed">{step.tagline}</p>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>

      {solution.hubspotFormId && (
        <div className="text-center mt-12">
          <Button variant="outline" className="rounded-full px-8 border-border text-muted-foreground hover:text-foreground" onClick={onCtaClick}>
            {t("getStartedToday")} <ArrowRight size={16} className="ml-2" />
          </Button>
        </div>
      )}
    </Section>
  );
};

export default SolutionSteps;
